import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListVendorBillSchedules,
  useCreateVendorBillSchedule,
  useUpdateVendorBillSchedule,
  useDeleteVendorBillSchedule,
  useGenerateVendorBills,
  useListInventoryVendors,
  useListLedgerAccounts,
  useListCostCentres,
  getListVendorBillSchedulesQueryKey,
} from "@workspace/api-client-react";
import type { VendorBillSchedule, TaxTreatment } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { inr, today } from "@/lib/format";
import { venId } from "@/lib/display-id";
import { TAX_TREATMENTS } from "@/components/expenses/bill-editor-sheet";

const FREQUENCIES = ["Monthly", "Weekly", "Quarterly", "Yearly"] as const;
const GST_RATES = [0, 5, 12, 18, 28, 40] as const;

interface ScheduleForm {
  name: string;
  costType: "Fixed" | "Variable";
  frequency: (typeof FREQUENCIES)[number];
  defaultTaxableAmount: string;
  gstRate: number;
  taxTreatment: TaxTreatment;
  vendorId: number | null;
  defaultExpenseAccountCode: string | null;
  defaultCostCentreId: number | null;
  isPrepaid: boolean;
  prepaidMonths: string;
  dueDayOfMonth: string;
  startDate: string;
  invoiceUrl: string | null;
  notes: string;
  status: "Active" | "Archived";
}

const EMPTY_FORM: ScheduleForm = {
  name: "",
  costType: "Fixed",
  frequency: "Monthly",
  defaultTaxableAmount: "",
  gstRate: 0,
  taxTreatment: "registered",
  vendorId: null,
  defaultExpenseAccountCode: null,
  defaultCostCentreId: null,
  isPrepaid: false,
  prepaidMonths: "",
  dueDayOfMonth: "",
  startDate: today(),
  invoiceUrl: null,
  notes: "",
  status: "Active",
};

/** 57-15's "incomplete" condition, mirrored client-side so the badge names
 * the exact gap the operator needs to close — never a mystery "incomplete". */
function needsMapping(s: {
  costType: string;
  defaultTaxableAmount: number | string | null;
  defaultExpenseAccountCode: string | null;
  defaultCostCentreId: number | null;
}): boolean {
  return (
    s.costType !== "Fixed" ||
    s.defaultTaxableAmount == null ||
    s.defaultTaxableAmount === "" ||
    s.defaultExpenseAccountCode == null ||
    s.defaultCostCentreId == null
  );
}

export interface SchedulesTabProps {
  /** Fired after "Generate bills" reports `incomplete > 0` — the parent
   * page switches to the Bills tab, pre-filtered to the zero-total drafts
   * this run just created. */
  onIncompleteGenerated?: () => void;
}

export function SchedulesTab({ onIncompleteGenerated }: SchedulesTabProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: schedules, isLoading } = useListVendorBillSchedules({});
  const { data: vendors } = useListInventoryVendors({});
  const { data: accounts } = useListLedgerAccounts({ type: "expense", active: true });
  const { data: costCentres } = useListCostCentres();
  const createMut = useCreateVendorBillSchedule();
  const updateMut = useUpdateVendorBillSchedule();
  const archiveMut = useDeleteVendorBillSchedule();
  const generateMut = useGenerateVendorBills();

  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<VendorBillSchedule | null>(null);
  const [archiveRow, setArchiveRow] = useState<VendorBillSchedule | null>(null);
  const [form, setForm] = useState<ScheduleForm>(EMPTY_FORM);

  useEffect(() => {
    if (editRow) {
      setForm({
        name: editRow.name,
        costType: editRow.costType,
        frequency: editRow.frequency,
        defaultTaxableAmount: editRow.defaultTaxableAmount != null ? String(editRow.defaultTaxableAmount) : "",
        gstRate: editRow.gstRate ?? 0,
        taxTreatment: editRow.taxTreatment,
        vendorId: editRow.vendorId,
        defaultExpenseAccountCode: editRow.defaultExpenseAccountCode,
        defaultCostCentreId: editRow.defaultCostCentreId,
        isPrepaid: editRow.isPrepaid,
        prepaidMonths: editRow.prepaidMonths != null ? String(editRow.prepaidMonths) : "",
        dueDayOfMonth: editRow.dueDayOfMonth != null ? String(editRow.dueDayOfMonth) : "",
        startDate: editRow.startDate.slice(0, 10),
        invoiceUrl: editRow.invoiceUrl,
        notes: editRow.notes ?? "",
        status: editRow.status,
      });
      setOpen(true);
    }
  }, [editRow]);

  function close() {
    setOpen(false);
    setEditRow(null);
    setForm(EMPTY_FORM);
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: getListVendorBillSchedulesQueryKey() });
  }

  async function save() {
    if (!form.name.trim()) {
      toast({ title: "Name is required.", variant: "destructive" });
      return;
    }
    if (form.vendorId == null) {
      toast({ title: "A vendor is required — a schedule can't generate a bill without one.", variant: "destructive" });
      return;
    }
    const payload = {
      name: form.name.trim(),
      vendorId: form.vendorId,
      costType: form.costType,
      frequency: form.frequency,
      defaultTaxableAmount: form.costType === "Fixed" && form.defaultTaxableAmount !== "" ? Number(form.defaultTaxableAmount) : null,
      gstRate: form.gstRate || null,
      taxTreatment: form.taxTreatment,
      defaultExpenseAccountCode: form.defaultExpenseAccountCode,
      defaultCostCentreId: form.defaultCostCentreId,
      isPrepaid: form.isPrepaid,
      prepaidMonths: form.isPrepaid ? Number(form.prepaidMonths) : null,
      dueDayOfMonth: form.dueDayOfMonth !== "" ? Number(form.dueDayOfMonth) : null,
      startDate: form.startDate,
      invoiceUrl: form.invoiceUrl,
      notes: form.notes || null,
    };
    try {
      if (editRow) await updateMut.mutateAsync({ id: editRow.id, data: { ...payload, status: form.status } });
      else await createMut.mutateAsync({ data: payload });
      toast({ title: editRow ? "Schedule updated." : "Schedule added." });
      refresh();
      close();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Something went wrong.", variant: "destructive" });
    }
  }

  async function confirmArchive() {
    if (!archiveRow) return;
    try {
      await archiveMut.mutateAsync({ id: archiveRow.id });
      toast({ title: `${archiveRow.name} archived.` });
      refresh();
      setArchiveRow(null);
    } catch {
      toast({ title: "Could not archive.", variant: "destructive" });
      setArchiveRow(null);
    }
  }

  async function handleGenerate() {
    try {
      const result = await generateMut.mutateAsync();
      // `{ created, skipped, incomplete }` on the wire — 57-09's generated
      // contract only declares `{ created, skipped }` (same documented gap
      // as this plan's other divergent endpoints).
      const withIncomplete = result as { created: number; skipped: number; incomplete?: number };
      refresh();
      const incomplete = withIncomplete.incomplete ?? 0;
      toast({
        title: `Generated ${withIncomplete.created}, skipped ${withIncomplete.skipped}${incomplete > 0 ? `, ${incomplete} incomplete` : ""}.`,
        description: incomplete > 0 ? "Some bills landed as zero-total drafts — map their expense account before submitting." : undefined,
        variant: incomplete > 0 ? "destructive" : "default",
      });
      if (incomplete > 0) onIncompleteGenerated?.();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not generate bills.", variant: "destructive" });
    }
  }

  const vendorName = (id: number | null) => vendors?.find((v) => v.id === id)?.vendorName ?? "—";
  const accountLabel = (code: string | null) => {
    if (!code) return null;
    const a = accounts?.find((x) => x.code === code);
    return a ? `${a.code} · ${a.name}` : code;
  };
  const costCentreName = (id: number | null) => costCentres?.find((c) => c.id === id)?.name ?? null;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleGenerate} disabled={generateMut.isPending}>
          {generateMut.isPending ? "Generating…" : "Generate bills"}
        </Button>
        <Button
          onClick={() => {
            setEditRow(null);
            setForm(EMPTY_FORM);
            setOpen(true);
          }}
        >
          Add Schedule
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Mapping</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (schedules ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No schedules yet. Click "Add Schedule".
                  </TableCell>
                </TableRow>
              ) : (
                schedules!.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sm">
                      {s.vendorId != null ? `${venId(s.vendorId)} · ${vendorName(s.vendorId)}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{s.frequency}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {s.costType === "Fixed" && s.defaultTaxableAmount != null ? inr(s.defaultTaxableAmount) : "—"}
                    </TableCell>
                    <TableCell>
                      {needsMapping(s) ? (
                        <Badge variant="destructive">Needs mapping</Badge>
                      ) : (
                        <Badge variant="secondary">{accountLabel(s.defaultExpenseAccountCode)} / {costCentreName(s.defaultCostCentreId)}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.status === "Archived" ? (
                        <Badge variant="outline" className="text-muted-foreground">
                          Archived
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditRow(s)}>
                        Edit
                      </Button>
                      {s.status === "Active" && (
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setArchiveRow(s)}>
                          Archive
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={open} onOpenChange={(o) => !o && close()}>
        <SheetContent side="right" className="w-[480px] flex flex-col overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editRow ? `Edit ${editRow.name}` : "Add Schedule"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-4 py-4">
            <div>
              <label className="text-xs font-semibold">
                Name <span className="text-destructive">*</span>
              </label>
              <Input placeholder="e.g. Floor Rent" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold">
                Vendor <span className="text-destructive">*</span>
              </label>
              <Select value={form.vendorId != null ? String(form.vendorId) : ""} onValueChange={(v) => setForm((p) => ({ ...p, vendorId: Number(v) }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {(vendors ?? []).map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      {venId(v.id)} · {v.vendorName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Cost Type</label>
                <Select value={form.costType} onValueChange={(v) => setForm((p) => ({ ...p, costType: v as "Fixed" | "Variable" }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fixed">Fixed (same each period)</SelectItem>
                    <SelectItem value="Variable">Variable (by usage)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold">Frequency</label>
                <Select value={form.frequency} onValueChange={(v) => setForm((p) => ({ ...p, frequency: v as (typeof FREQUENCIES)[number] }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">{form.costType === "Fixed" ? "Amount ₹" : "Amount ₹ (variable)"}</label>
                <Input
                  type="number"
                  step="0.01"
                  disabled={form.costType === "Variable"}
                  value={form.defaultTaxableAmount}
                  onChange={(e) => setForm((p) => ({ ...p, defaultTaxableAmount: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold">GST %</label>
                <Select value={String(form.gstRate)} onValueChange={(v) => setForm((p) => ({ ...p, gstRate: Number(v) }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((r) => (
                      <SelectItem key={r} value={String(r)}>
                        {r}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Tax treatment</label>
              <Select value={form.taxTreatment} onValueChange={(v) => setForm((p) => ({ ...p, taxTreatment: v as TaxTreatment }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_TREATMENTS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Default expense account</label>
                <Select
                  value={form.defaultExpenseAccountCode ?? "none"}
                  onValueChange={(v) => setForm((p) => ({ ...p, defaultExpenseAccountCode: v === "none" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not mapped" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not mapped —</SelectItem>
                    {(accounts ?? []).map((a) => (
                      <SelectItem key={a.id} value={a.code}>
                        {a.code} · {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold">Default cost centre</label>
                <Select
                  value={form.defaultCostCentreId != null ? String(form.defaultCostCentreId) : "none"}
                  onValueChange={(v) => setForm((p) => ({ ...p, defaultCostCentreId: v === "none" ? null : Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not mapped" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not mapped —</SelectItem>
                    {(costCentres ?? []).filter((c) => c.active).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.code} · {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {needsMapping(form) && (
              <p className="text-[11px] text-muted-foreground">
                Without a fixed amount, expense account and cost centre this schedule generates zero-total draft
                bills that need mapping before they can be submitted.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Start From</label>
                <Input type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold">Due day (monthly)</label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={form.dueDayOfMonth}
                  onChange={(e) => setForm((p) => ({ ...p, dueDayOfMonth: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="schedule-prepaid"
                checked={form.isPrepaid}
                onChange={(e) => setForm((p) => ({ ...p, isPrepaid: e.target.checked }))}
              />
              <label htmlFor="schedule-prepaid" className="text-xs font-semibold">
                Prepaid — recognise generated bills over multiple months
              </label>
            </div>
            {form.isPrepaid && (
              <div>
                <label className="text-xs font-semibold">Months (min 2)</label>
                <Input type="number" min={2} value={form.prepaidMonths} onChange={(e) => setForm((p) => ({ ...p, prepaidMonths: e.target.value }))} />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            {editRow && (
              <div>
                <label className="text-xs font-semibold">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v as "Active" | "Archived" }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button onClick={save} disabled={createMut.isPending || updateMut.isPending}>
              {createMut.isPending || updateMut.isPending ? "Saving…" : editRow ? "Save Changes" : "Add"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={archiveRow !== null} onOpenChange={(o) => !o && setArchiveRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiveRow?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Bills it already generated are kept — archiving only stops future generation. This mirrors the legacy
              module's own no-hard-delete precedent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmArchive} disabled={archiveMut.isPending}>
              {archiveMut.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
